import type { Deliverable, ISODate, Period } from "./types";
import { fromDay, toDay } from "./dates";

export interface AutoForecastResult {
  deliverable: Deliverable;
  /** 0-based step indices whose forecast was derived. */
  derived: number[];
  problem?: string;
}

/**
 * Rule 22 (R40, R46): with auto-forecast on, the last milestone's date is locked and the forecast dates of the
 * unearned milestones between the last earned one and the last one are spread from the current cutoff, never
 * passing the locked date. Spacing is open Q3, so both options are offered:
 *  - "equal": equal gaps between the cutoff and the locked date
 *  - "proportional": the same proportions as the planned dates
 */
export function applyAutoForecast(
  d: Deliverable,
  periods: Period[],
  currentIdx: number,
  spacing: "equal" | "proportional",
): AutoForecastResult {
  const steps = d.steps;
  const n = steps.length;
  if (n < 2 || !periods[currentIdx]) return { deliverable: d, derived: [] };
  const last = steps[n - 1];
  if (last.earned) return { deliverable: d, derived: [] };
  const locked: ISODate | null = last.forecast ?? last.planned ?? null;
  if (!locked) return { deliverable: d, derived: [], problem: "The last milestone has no forecast or planned date to lock" };
  let lastEarned = -1;
  steps.forEach((s, i) => {
    if (s.earned) lastEarned = i;
  });
  // Only deliverables that have started are re-spread (Cesar, Sep 25: "if I started earning the 1st and 2nd
  // milestones..."; Sep 27 video: forecast matches the plan until progress starts).
  if (lastEarned < 0) return { deliverable: d, derived: [] };
  const between: number[] = [];
  for (let i = lastEarned + 1; i < n - 1; i++) between.push(i);
  if (!between.length) return { deliverable: d, derived: [] };

  const cutoff = toDay(periods[currentIdx].cutoff);
  const end = toDay(locked);
  if (end <= cutoff) return { deliverable: d, derived: [], problem: `Locked last date ${locked} is not after the current cutoff` };

  const m = between.length + 1; // gaps from cutoff to the locked date
  let fractions = between.map((_, i) => (i + 1) / m);
  if (spacing === "proportional") {
    const anchorStep = lastEarned >= 0 ? steps[lastEarned] : undefined;
    const pa = anchorStep?.planned ?? steps[between[0]].planned;
    const pl = last.planned;
    const ok = pa && pl && toDay(pl) > toDay(pa) && between.every((i) => steps[i].planned);
    if (ok) {
      const a = toDay(pa!);
      const span = toDay(pl!) - a;
      fractions = between.map((i) => Math.min(1, Math.max(0, (toDay(steps[i].planned!) - a) / span)));
    }
  }
  const newSteps = steps.map((s) => ({ ...s }));
  let prev = cutoff;
  between.forEach((i, k) => {
    let day = cutoff + Math.round(fractions[k] * (end - cutoff));
    day = Math.min(end, Math.max(day, cutoff + 1, prev));
    prev = day;
    newSteps[i].forecast = fromDay(day);
  });
  return { deliverable: { ...d, steps: newSteps }, derived: between };
}
